import { Tabs, Checkbox, TextField } from '@shopify/polaris';
import { Popover, Button } from '@superops/ui-design-system';
import { useQuery, useMutation } from '@apollo/client';
import React, { useCallback, useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import { Stopwatch } from '@styled-icons/fa-solid/Stopwatch';
import { cloneDeep, pick, includes, isEmpty } from 'lodash';
import { ShoppingCart } from '@styled-icons/fa-solid/ShoppingCart';
import { Close } from '@styled-icons/evaicons-solid';
import { ContentWrapper } from 'sopslibrary';
import {
  GET_WHITELISTED_OFFERINGS,
  GET_WHITELISTED_SERVICE_ITEMS,
  AccountClient,
  CREATE_NEW_WORKLOG,
  CREATE_NEW_WORKLOG_TIMER,
  UPDATE_CURRENT_WORKLOG_TIMER,
  GET_ALL_WORKLOG_TIMERS
} from '@nile/graphql';
import {
  DropdownIndicator,
  formatGroupLabel,
  IndicatorSeparator
} from '@nile/components/src/Pebbles/select';
import {
  getParsedCrudData,
  getTranslatedText,
  BackgroundIcon,
  EventEmitter,
  toast,
  AccessControl,
  TEXT_LENGTH,
  Loader,
  AppContext,
  isAuthorised
} from '@nile/components';
import { WORKLOG_TIMER_STATE } from '@nile/components/src/Constants/worklog';
import Select from 'react-select';
import Timer from 'react-compound-timer';
import envConfig from '@nile/home/src/Config/env';
import DatePicker from 'react-datepicker';
import dayjs from 'dayjs';
import { FormattedMessage, injectIntl } from 'react-intl';
import nanoid from 'nanoid';
import { SVGIcon } from '@superops/super-icons';

const CloseIcon = styled(Close)`
  min-width: 16px;
  min-height: 16px;
  width: 16px;
  height: 16px;
  color: black;
  cursor: pointer;
`;

const WorklogWrapper = styled.div`
  min-width: 700px;
  .Polaris-Button--plain {
    padding: 0px !important;
    margin: 0 !important;
  }
`;

const ItemWrap = styled.div`
  padding: 8px 0;
  .Polaris-TextField {
    margin-bottom: 0px !important;
    max-width: 40px !important;
    margin-right: 8px;
  }
  .quantity .Polaris-TextField {
    max-width: 100px !important;
  }
`;

const ActionButtons = styled.div`
  .Polaris-Button {
    min-height: 2.6rem;
    height: 35px;
    border-radius: 50%;
    padding: 0 !important;
  }
`;

function WorklogContainer(props) {
  const { intl, client, inputVariable } = props;
  const appContext = useContext(AppContext);
  const { user } = appContext;
  const roleFeatures = user?.role?.roleFeatureList || [];
  const isItemsAuthorised = isAuthorised(roleFeatures, 'serviceCatalog.view');
  const [popoverActive, setPopoverActive] = useState(false);
  const [allServiceItems, setAllServiceItems] = useState([]);
  const { loading, data, error } = useQuery(GET_WHITELISTED_SERVICE_ITEMS, {
    client: AccountClient,
    fetchPolicy: 'cache-and-network',
    skip: !isItemsAuthorised,
    variables: {
      listInfo: {},
      clientId: client.accountId
    },
    onCompleted(res) {
      setAllServiceItems(res?.getWhitelistedServiceItems?.items || []);
    }
  });
  const [createWorklogTimer] = useMutation(CREATE_NEW_WORKLOG_TIMER);
  const [updateWorklogTimer] = useMutation(UPDATE_CURRENT_WORKLOG_TIMER);
  const togglePopoverActive = useCallback(
    () => setPopoverActive((active) => !active),
    []
  );

  const createWorklogTimerAction = (params, callback) => {
    const inputVariables = { worklogTimer: params.timerInput };
    inputVariables[inputVariable] = props.id;
    createWorklogTimer({
      variables: inputVariables,
      input: {
        worklogTimer: params.timerInput
      }
    }).then((res) => {
      if (callback && res?.data?.createWorklogTimer) {
        callback(res.data.createWorklogTimer, params.itemId);
      }
    });
  };

  const updateWorklogTimerAction = (params, callback) => {
    updateWorklogTimer({
      variables: {
        timerId: params.timerId,
        worklogTimer: params.timerInput
      },
      input: {
        worklogTimer: params.timerInput
      }
    }).then((res) => {
      if (callback && res?.data?.updateWorklogTimer) {
        callback(res.data.updateWorklogTimer, params.itemId);
      }
    });
  };

  const actions = {
    createWorklogTimer: createWorklogTimerAction,
    updateWorklogTimer: updateWorklogTimerAction
  };

  const activator = (
    <Button onClick={togglePopoverActive}>
      <div className="fx center">
        <SVGIcon name="Worklog" />
      </div>
    </Button>
  );

  return (
    <div className="vignesh">
      <Popover
        active={popoverActive}
        activator={activator}
        onClose={() => {
          return;
        }}
        preferredAlignment="right"
      >
        <ContentWrapper bgcolor="#ffffff">
          <Worklog
            intl={intl}
            togglePopoverActive={togglePopoverActive}
            allServiceItems={allServiceItems}
            moduleId={props.id}
            inputVariable={inputVariable}
            actions={actions}
            ticket={props.ticket}
          />
        </ContentWrapper>
      </Popover>
    </div>
  );
}

function Worklog(props) {
  const { intl, moduleId, inputVariable } = props;
  const inputVariables = {};
  inputVariables[inputVariable] = moduleId;
  const {
    loading: timerLoading,
    data: allTimers,
    error: timerError
  } = useQuery(GET_ALL_WORKLOG_TIMERS, {
    variables: inputVariables,
    fetchPolicy: 'network-only',
    onCompleted(res) {
      const itemsWithSplitTimers = [];
      // If we have items with timespent and timespentAfterHours value, we need to split them into two different items.
      // Both will have same service item id. So nanoid is given as itemId.
      res.getWorklogTimers.forEach((el) => {
        itemsWithSplitTimers.push({
          ...el,
          itemId: `${nanoid()}`
        });
        if (
          el.timespentAfterHours &&
          el.state === WORKLOG_TIMER_STATE.stopped
        ) {
          itemsWithSplitTimers.push({
            ...el,
            itemId: `${nanoid()}`,
            timespent: el.timespentAfterHours,
            afterHours: true
          });
        }
      });
      setItems(itemsWithSplitTimers);
    }
  });
  const [items, setItems] = useState([]);
  const [referenceItems, setReferenceItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [resetTimer, setResetTimer] = useState(false);
  const [worklogDate, setWorklogDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [addItems, setAddItems] = useState([]);
  const [updateItems, setupdateItems] = useState([]);
  const [deleteItems, setdeleteItems] = useState([]);
  const { allServiceItems, id } = props;
  const [createWorklog] = useMutation(CREATE_NEW_WORKLOG);
  useEffect(() => {
    if (resetTimer) {
      addWorklog();
    }
  }, [resetTimer]);
  if (timerLoading)
    return (
      <WorklogWrapper>
        <div className="fx actr jcenter h-250 w100 h-300">
          <Loader />
        </div>
      </WorklogWrapper>
    );
  const addItem = () => {
    const allItems = cloneDeep(items);
    // Add unique id for each item on addition and use them for comparison.
    allItems.push({
      itemId: `${nanoid()}`,
      serviceItem: '',
      billable: true,
      afterHours: false,
      qty: null
    });
    setItems(allItems);
  };
  const refreshItem = (item, itemId) => {
    const allItems = cloneDeep(items);
    const index = allItems.findIndex((el) => el.itemId === itemId);
    allItems[index] = { ...item, itemId: itemId };
    setItems(allItems);
  };
  const createWorklogTimerAction = (params) => {
    props.actions.createWorklogTimer(params, refreshItem);
  };
  const refreshItemOnUpdate = (item, itemId) => {
    // If the timer is stopped, in the response, we check if it's afterhours and create a new item.
    if (item.state === WORKLOG_TIMER_STATE.stopped) {
      const allItems = cloneDeep(items);
      const index = allItems.findIndex((el) => el.itemId === itemId);
      allItems[index] = { ...item, itemId: itemId };
      if (item.timespentAfterHours) {
        allItems.push({
          ...item,
          itemId: `${nanoid()}`,
          afterHours: true,
          timespent: item.timespentAfterHours
        });
      }
      setItems(allItems);
    } else {
      refreshItem(item, itemId);
    }
  };
  const updateWorklogTimerAction = (params) => {
    props.actions.updateWorklogTimer(params, refreshItemOnUpdate);
  };
  const actions = {
    createWorklogTimer: createWorklogTimerAction,
    updateWorklogTimer: updateWorklogTimerAction
  };
  const updateItem = (item, field, value) => {
    const allItems = cloneDeep(items);
    const allItemIndex = items.findIndex((el) => {
      return el.itemId === item.itemId;
    });
    if (allItemIndex > -1) {
      allItems[allItemIndex][field] = value;
    }
    setItems(allItems);
  };

  /**
   * If the unit type is quantity unit is just a string.
   * Else we need to convert it to hours and send it to backend.
   * It can be manual entry or it can be after stopping the worklog timer.
   */
  const getQuantity = (el) => {
    if (el.qty) {
      if (el?.serviceItem?.quantityType !== 'UNITS') {
        return String(el.qty / 60);
      }
      return String(el.qty);
    }
    if (el.timespent) return String(el.timespent / 60) || '0';
    return '0';
  };

  const addWorklog = () => {
    const reqItems = items
      .filter(
        (el) =>
          !isEmpty(el.serviceItem) &&
          (!el.timerId ||
            (el.timerId && el.state === WORKLOG_TIMER_STATE.stopped))
      )
      .map((el) => {
        return {
          ...el,
          itemId: el.itemId
        };
      });
    const baseObj = referenceItems.map((el) => {
      return {
        ...el,
        itemId: el.itemId
      };
    });
    const parsedObject = getParsedCrudData(baseObj, reqItems, 'itemId');
    const newAddedItems = parsedObject.addedItems;
    const addedItems = newAddedItems.map((el) => {
      return {
        billable: el.billable,
        qty: getQuantity(el),
        serviceItem: pick(el.serviceItem, ['itemId', 'name']),
        afterHours: el.afterHours
      };
    });
    props.togglePopoverActive();
    const inputParams = {
      worklog: {
        notes: notes,
        worklogDate: worklogDate,
        addItems: addedItems,
        resetTimer: resetTimer
      }
    };
    inputParams[inputVariable] = moduleId;
    createWorklog({
      variables: inputParams,
      input: {
        worklog: {
          notes: notes,
          addItems: addedItems,
          worklogDate: worklogDate,
          resetTimer: resetTimer
        }
      }
    }).then((res) => {
      if (res?.data?.createWorklog) {
        EventEmitter.dispatch('worklogChange');
        toast('success', 'ticket.worklog.creation.success');
      } else {
        // toast('error', 'Something went wrong');
      }
    });
  };
  const resetTimerBool = () => {
    setResetTimer(true);
  };
  const hasTimers = items.some(
    (el) => el.timerId && includes(['RUNNING', 'PAUSED'], el.state)
  );
  return (
    <WorklogWrapper>
      <div className="bold flex">
        <h2>
          <FormattedMessage id="ticket.details.worklog.header" />
        </h2>
        <div className="ml8 flex center">
          <div className="fx center ml8 mr8">
            <SVGIcon name="DatePicker" />
          </div>
          <DatePicker
            selected={worklogDate && dayjs(worklogDate).toDate()}
            onChange={(date) => {
              const formattedDate = dayjs(date).format('YYYY-MM-DD');
              setWorklogDate(formattedDate);
            }}
            placeholderText="YYYY-MM-DD"
            dateFormat="yyyy-MM-dd"
            title={getTranslatedText(intl, 'worklog.details.worklogDate')}
            required
            disabled={false}
          />
        </div>
        <div className="mlauto mr8" onClick={props.togglePopoverActive}>
          <CloseIcon />
        </div>
      </div>
      {items.map((item) => {
        return (
          <Item
            intl={intl}
            item={item}
            allServiceItems={allServiceItems}
            updateItem={updateItem}
            actions={actions}
            allItems={items}
          />
        );
      })}
      <div className="mt20">
        <Button
          plain
          onClick={addItem}
          icon={
            <div className="fx center">
              <SVGIcon name="Add" color="#011FFF" />
            </div>
          }
        >
          <FormattedMessage id="ticket.details.worklog.newItem" />
        </Button>
      </div>
      <div className="mt20">
        <TextField
          label=""
          placeholder={getTranslatedText(intl, 'ticket.details.worklog.pl')}
          value={notes}
          onChange={(val) => setNotes(val)}
          multiline={3}
          maxLength={TEXT_LENGTH.size_9}
        />
      </div>
      <div className="flex mt20 mb10">
        <div className="mlauto">
          {hasTimers ? (
            <Button
              primary
              onClick={addWorklog}
              connectedDisclosure={{
                accessibilityLabel: 'Other save actions',
                actions: [
                  {
                    content: (
                      <div onClick={resetTimerBool}>
                        <FormattedMessage id="ticket.details.worklog.stopTimer" />
                      </div>
                    )
                  }
                ]
              }}
            >
              <FormattedMessage id="ticket.details.worklog.addWorklog" />
            </Button>
          ) : (
            <Button id="addPSAWorklog" primary onClick={addWorklog}>
              <FormattedMessage id="ticket.details.worklog.addWorklog" />
            </Button>
          )}
        </div>
      </div>
    </WorklogWrapper>
  );
}

function Item(props) {
  const { intl, item, allServiceItems, actions, allItems } = props;
  const handleBillableChange = useCallback((newChecked) => {
    props.updateItem(item, 'billable', newChecked);
  });
  const handleOfferingChange = (itemId, val) => {
    props.updateItem({ ...item, itemId: itemId }, 'serviceItem', val);
  };
  const handleQuantityChange = (val) => {
    props.updateItem(item, 'qty', val);
  };
  const options = allServiceItems.map((el) => {
    return {
      ...el,
      name: el.name,
      label: el.name,
      value: el.name
    };
  });
  const startTimer = () => {
    props.actions.createWorklogTimer({
      timerInput: {
        serviceItem: {
          itemId: item?.serviceItem.itemId,
          name: item?.serviceItem.name
        },
        billable: !!item?.billable
      },
      itemId: item.itemId
    });
  };
  const updateTimer = (event) => {
    props.actions.updateWorklogTimer({
      timerInput: {
        serviceItem: {
          itemId: item?.serviceItem.itemId,
          name: item?.serviceItem.name
        },
        billable: !!item?.billable,
        event: event
      },
      timerId: item.timerId,
      itemId: item.itemId
    });
  };

  const renderActionButtons = (
    selectedItem,
    getTimerState,
    resume,
    pause,
    stop,
    start,
    reset,
    timerState
  ) => {
    if (selectedItem.state) {
      switch (selectedItem.state) {
        case 'RUNNING':
          return (
            <div className="flex">
              <div className="ml8">
                <Button id="psaPauseWorklog" onClick={pause}>
                  <div id="timer--pause" className="fx center">
                    <SVGIcon name="PausePlain" size="xs" color="#333333" />
                  </div>
                </Button>
              </div>
              <div className="ml8">
                <Button id="psaStopWorklog" onClick={stop}>
                  <div id="timer--stop" className="fx center">
                    <SVGIcon name="Stop" size="xs" />
                  </div>
                </Button>
              </div>
            </div>
          );
        case 'PAUSED':
          return (
            <div className="flex">
              <div className="ml8">
                <Button id="psaStartWorklogTimer" onClick={resume}>
                  <div id="timer--start" className="fx center">
                    <SVGIcon name="Play" size="xs" />
                  </div>
                </Button>
              </div>
              <div className="ml8">
                <Button id="psaStopWorklogTimer" onClick={stop}>
                  <div id="timer--stop" className="fx center">
                    <SVGIcon name="Stop" size="xs" />
                  </div>
                </Button>
              </div>
            </div>
          );
        default:
          return '';
      }
    } else {
      switch (getTimerState()) {
        case 'PLAYING':
          return (
            <div className="flex">
              <div className="ml8">
                <Button id="psaPauseWorklogTimer2" onClick={pause}>
                  <div id="timer--pause" className="fx center">
                    <SVGIcon name="PausePlain" size="xs" color="#333333" />
                  </div>
                </Button>
              </div>
              <div className="ml8">
                <Button id="psaStopWorklogTimer2" onClick={stop}>
                  <div id="timer--stop" className="fx center">
                    <SVGIcon name="Stop" size="xs" />
                  </div>
                </Button>
              </div>
            </div>
          );
        case 'PAUSED':
          return (
            <>
              <div className="ml8">
                <Button onClick={resume}>
                  <div id="timer--start" className="fx center">
                    <SVGIcon name="Play" size="xs" />
                  </div>
                </Button>
              </div>
              <div className="ml8">
                <Button onClick={stop}>
                  <div id="timer--stop" className="fx center">
                    <SVGIcon name="Stop" size="xs" />
                  </div>
                </Button>
              </div>
            </>
          );
        case 'INITED':
          return (
            <>
              <Button onClick={start}>
                <div id="timer--start" className="fx center">
                  <SVGIcon name="Play" size="xs" />
                </div>
              </Button>
            </>
          );
        default:
          return '';
      }
    }
  };
  const getHours = (duration) => {
    const hours = Math.floor(duration / (1000 * 60 * 60));

    return hours;
  };
  const getMinutes = (duration) => {
    const minutes = Math.round((duration / (1000 * 60)) % 60);
    return minutes;
  };
  const handleDurationChange = (type, val, totalTime) => {
    const minutes = getMinutes(totalTime);
    const hours = getHours(totalTime);
    let updatedQuantity = hours * 60 + minutes;
    switch (type) {
      case 'hours':
        updatedQuantity = Number(val) * 60 + minutes;
        break;
      case 'minutes':
        updatedQuantity = Number(hours) * 60 + Number(val);
        break;
      default:
        break;
    }
    props.updateItem(item, 'qty', updatedQuantity);
  };
  const renderQuantity = (selectedItem) => {
    if (
      selectedItem?.serviceItem?.quantityType &&
      selectedItem?.serviceItem?.quantityType !== 'UNITS'
    ) {
      // If the timer is running and if it contains both after hours and current hours,
      // we will show as single row only. So the time has to be added.
      const initialTimeForSplitTimers =
        selectedItem.state !== WORKLOG_TIMER_STATE.stopped
          ? (selectedItem?.timespent + selectedItem?.timespentAfterHours) *
            60000
          : selectedItem?.timespent * 60000;
      return (
        <Timer
          initialTime={
            Number(selectedItem?.qty) * 60000 || initialTimeForSplitTimers || 0
          }
          startImmediately={selectedItem.state === 'RUNNING' || false}
          lastUnit="h"
          onStart={() => startTimer()}
          onResume={() => updateTimer('RESUME')}
          onPause={() => updateTimer('PAUSE')}
          onStop={() => updateTimer('STOP')}
          onReset={() => console.log('onReset hook')}
          timeToUpdate={
            Number(selectedItem?.qty) * 60000 || initialTimeForSplitTimers || 0
          }
        >
          {(params) => {
            const {
              start,
              resume,
              pause,
              stop,
              reset,
              timerState,
              getTimerState,
              getTime
            } = params;
            const runTime = getTime();
            return (
              <>
                <div className="flex center">
                  <div className="fx center ml8">
                    <SVGIcon name="Worklog" />
                  </div>
                  <div className="ml8 mr8">
                    {(timerState === WORKLOG_TIMER_STATE.stopped ||
                      timerState === 'INITED') &&
                    selectedItem.state !== 'RUNNING' ? (
                      <TextField
                        value={
                          selectedItem?.qty >= 0
                            ? getHours(selectedItem.qty * 60000)?.toString()
                            : getHours(initialTimeForSplitTimers)?.toString()
                        }
                        onChange={(val) => {
                          handleDurationChange(
                            'hours',
                            val,
                            selectedItem?.qty >= 0
                              ? selectedItem.qty * 60000
                              : getTime()
                          );
                        }}
                        type="number"
                      />
                    ) : (
                      <div className="mr4">
                        <Timer.Hours />
                      </div>
                    )}
                    <div className="fs10 uppercase">
                      <FormattedMessage id="ticket.details.worklog.hrs" />
                    </div>
                  </div>
                  <div className="align-self-start disabled">:</div>
                  <div className="ml8 mr8">
                    {(timerState === WORKLOG_TIMER_STATE.stopped ||
                      timerState === 'INITED') &&
                    selectedItem.state !== 'RUNNING' ? (
                      <TextField
                        value={
                          selectedItem?.qty >= 0
                            ? getMinutes(selectedItem.qty * 60000)?.toString()
                            : getMinutes(initialTimeForSplitTimers)?.toString()
                        }
                        onChange={(val) => {
                          handleDurationChange(
                            'minutes',
                            val,
                            selectedItem?.qty >= 0
                              ? selectedItem.qty * 60000
                              : getTime()
                          );
                        }}
                        type="number"
                      />
                    ) : (
                      <div className="mr4">
                        <Timer.Minutes />
                      </div>
                    )}
                    <div className="fs10 uppercase">
                      <FormattedMessage id="ticket.details.worklog.min" />
                    </div>
                  </div>
                  {/* <div className="flex center mr8">
                    <div className="mr4">
                      <Timer.Seconds />
                    </div>
                    <FormattedMessage id="ticket.details.worklog.sec" />
                  </div> */}
                </div>
                <br />
                <ActionButtons>
                  {renderActionButtons(
                    selectedItem,
                    getTimerState,
                    resume,
                    pause,
                    stop,
                    start,
                    reset,
                    timerState
                  )}
                </ActionButtons>
              </>
            );
          }}
        </Timer>
      );
    }
    if (selectedItem?.serviceItem) {
      return (
        <div className="quantity ml8 flex center">
          <div className="fx center mr8">
            <SVGIcon name="Quantity" />
          </div>
          <TextField
            value={item.qty}
            placeholder={getTranslatedText(
              intl,
              'ticket.details.worklog.quantity'
            )}
            maxLength={10}
            onChange={handleQuantityChange}
            type="number"
          />
        </div>
      );
    }
    return '';
  };
  return (
    <ItemWrap className="flex center">
      <div className="fx center mr8">
        <SVGIcon name="Cart" />
      </div>
      <div className="mr8">
        <Select
          className="rule-fields"
          value={options.find((el) => el.itemId === item?.serviceItem?.itemId)}
          onChange={handleOfferingChange.bind(this, item.itemId)}
          classNamePrefix="value_selector"
          formatGroupLabel={formatGroupLabel}
          components={{ IndicatorSeparator, DropdownIndicator }}
          options={options}
          menuPortalTarget={document.body}
          menuPlacement="auto"
          closeMenuOnScroll={(e) => {
            return (
              e.target &&
              e.target.classList &&
              !(e.target.classList.value.indexOf('menu-list') > -1)
            );
          }}
          name="valueselector"
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            menu: (base) => ({ ...base, zIndex: 9999 })
          }}
        />
      </div>
      <AccessControl
        roleToCheck="ticketMarkWorklogAsBillable.allow"
        render={
          <div className="mr8">
            <Checkbox
              label={getTranslatedText(intl, 'ticket.details.worklog.billable')}
              checked={item.billable}
              onChange={handleBillableChange}
            />
          </div>
        }
      />

      {renderQuantity(item)}
      {item?.serviceItem?.quantityType && // After hours option should come only for items which are not of unit type and if the timer is stopped/not started
        item?.serviceItem?.quantityType !== 'UNITS' &&
        !includes(
          [WORKLOG_TIMER_STATE.running, WORKLOG_TIMER_STATE.paused],
          item.state
        ) && (
          <div className="ml16">
            <Checkbox
              label={getTranslatedText(
                intl,
                'ticket.details.worklog.afterHours'
              )}
              checked={item.afterHours}
              onChange={(newChecked) => {
                props.updateItem(item, 'afterHours', newChecked);
              }}
            />
          </div>
        )}
    </ItemWrap>
  );
}

export default injectIntl(WorklogContainer);
